import { useMutationState } from "@tanstack/react-query";
import { ButtonGroup } from "@tql/ui-components-react/kendo/buttons";
import { SvgIcon } from "@tql/ui-components-react/kendo/common";
import { Button } from "@tql/ui-components-react/tql/buttons";
import { xClose } from "@tql/ui-components-react/untitled/icons";
import { useState } from "react";
import { useParams } from "react-router";

import { LoadingSpinner } from "~/shared/components/loading-spinner/loading-spinner.tsx";

import { usePostCancelDispatch } from "../../hooks/use-post-cancel-dispatch.ts";
import type { AugmentedShipment } from "../../types/augmented-shipment";
import { MarkLoadedModal } from "./components/mark-loaded-modal/index.tsx";
import * as styles from "./dispatch-group.module.css";

export const DispatchedGroup = ({
	shipment,
}: {
	shipment: AugmentedShipment;
}) => {
	const params = useParams();
	const poNumber = params["poNumber"] ?? "";
	const { cancelDispatch, isSavingCancelDispatch } = usePostCancelDispatch();
	const poNumberValue = Number(poNumber);
	// Observe the mark-loaded mutation across hook instances (it is fired from
	// inside the modal) so the spinner covers the save + shipment refetch.
	const isSavingMarkLoaded =
		useMutationState({
			filters: { mutationKey: ["markLoaded"] },
			select: (mutation) => mutation.state.status,
		}).at(-1) === "pending";
	const [isMarkLoadedOpen, setIsMarkLoadedOpen] = useState(false);

	const { carrier } = shipment;

	return (
		<ButtonGroup className={"shipment-button-group"}>
			<LoadingSpinner
				isVisible={isSavingMarkLoaded || isSavingCancelDispatch}
			/>
			{isMarkLoadedOpen ? (
				<MarkLoadedModal
					initialProNumber={carrier?.pro}
					onClose={() => setIsMarkLoadedOpen(false)}
				/>
			) : null}
			<div className={styles.infoGrid}>
				<div className={styles.infoPair}>
					<p className={styles.infoLabel}>Carrier:</p>
					<p className={styles.infoValue}>{carrier?.carrierName ?? "N/A"}</p>
				</div>
				<div className={styles.infoPair}>
					<p className={styles.infoLabel}>PRO Number:</p>
					<p className={styles.infoValue}>{carrier?.pro ?? "N/A"}</p>
				</div>
				<div className={styles.infoPair}>
					<p className={styles.infoLabel}>Carrier Confirmation #:</p>
					<p className={styles.infoValue}>
						{carrier?.confirmationNumber ?? "N/A"}
					</p>
				</div>
			</div>
			<Button
				disabled={isSavingMarkLoaded || isSavingCancelDispatch}
				onClick={() => setIsMarkLoadedOpen(true)}
				variant={"primary"}
			>
				Mark Loaded
			</Button>
			<Button
				disabled={isSavingMarkLoaded || isSavingCancelDispatch}
				onClick={() => cancelDispatch(poNumberValue)}
				variant={"dangerSecondary"}
			>
				<SvgIcon className={"shipment-progress-icon"} icon={xClose} />
				Cancel Dispatch
			</Button>
		</ButtonGroup>
	);
};
